import React, {
    useEffect,
    useRef,
    useMemo,
    useState
} from 'react';
import { parseISO, format,formatDate,sub } from "date-fns";
import Accordion from '../../scripts/components/Accordion'
import { useQuery, useQueries } from '@tanstack/react-query';


import * as OPTIONS from "../option-fields";
import * as UTILS from "../utils";
import * as QUERIES from "../query-functions";

import { getCustomer } from "../router";

import LoadingIndicator from '../components/loading-indicator';
import AccountNavigation from './account-navigation';
import UpsellBlock from '../components/upsell-block';
import LogoutButton from '../components/logout-button';
import UpcomingTickets from './upcoming-tickets';
import ExtrasInstance from '../extras/extras-instance';
import ArrowIcon from '../svg/arrow';

const AccountOrders = () => {

    const { data: customer } = getCustomer();

    const [upcomingTickets, setUpcomingTickets] = useState(null);
    const [extrasPending, setExtrasPending] = useState(null);
    const [extras, setExtras] = useState(null);
    const [currentExtras, setCurrentExtras] = useState(null);
    const [ordersDataLength, setOrdersDataLength] = useState(0);
    const [prevOrdersDataLength, setPrevOrdersDataLength] = useState(0);

    const accordionRef = useRef(null);

    const date_now = formatDate(Date.now(), "yyyy-MM-dd")
    const date_six_month_ago = formatDate(sub(Date.now(), { months: 6 }), "yyyy-MM-dd")

    const loadMoreOrders = () => {
        // Extend the date range back by another 6 months
        const newEnd = dateRange.start;
        const newStart = formatDate(sub(parseISO(newEnd), { months: 6 }), "yyyy-MM-dd");
        // Store previous length to compare after refetch
        setPrevOrdersDataLength(ordersDataLength);

        setDateRange({
            start: newStart,
            end: dateRange.end
        });
    };
        
    const [dateRange, setDateRange] = useState({
        start: date_six_month_ago,
        end: date_now
    });


    const { 
        data: ordersDataAuth,
    } = useQuery({
        queryKey: ['ordersDataAuth', customer.id, dateRange.start, dateRange.end],
        enabled: !!customer?.id,
        queryFn: () => QUERIES.getAuthenticatedOrders(customer.id,dateRange.start,dateRange.end,false), // if it is NOT a higher-order fn already
        // If getAuthenticatedOrders already returns a function, keep: queryFn: QUERIES.getAuthenticatedOrders(customer.id)
        refetchOnWindowFocus: false,
        retry: false,
    });

    const supplementaryEventData = useQueries({
        queries: upcomingTickets
        ? upcomingTickets.map((ticket) => ({
            queryKey: ['supplementaryEventData', ticket.instance.id],
            queryFn: () => QUERIES.getSupplementaryEvents(ticket.instance.id),
            enabled: true,
            staleTime: Infinity,
            refetchOnWindowFocus: false,
            retry: false
          }))
        : []
    });

    useEffect(() => {
        if (!ordersDataAuth?.length || !accordionRef?.current) return;

        setOrdersDataLength(ordersDataAuth.length);

        // Make sure the ordersDataAuth mapping has completed before initing the accordion
        setTimeout(() => {
            new Accordion(accordionRef.current);
        }, 1)
    }, [ordersDataAuth, accordionRef])

    useEffect(() => {
        if (!ordersDataAuth?.length) return;

        const upcoming = UTILS.getUpcomingTickets(ordersDataAuth);
        setUpcomingTickets(upcoming);

    }, [ordersDataAuth]);

    useEffect(() => {
        if (!supplementaryEventData?.length || !upcomingTickets?.length) return;

        const is_pending = supplementaryEventData.some(query => query.isLoading);
        setExtrasPending(is_pending);

        const data = supplementaryEventData.map((result, index) => {
            if (result.data) return {
                instance: upcomingTickets[index], 
                extras: result.data
            }
        }).filter((notUndefined) => notUndefined !== undefined);

        // Only set extras if the array has changed to stop infinite rerenders
        setExtras((prev) => {
            const isSame =
                prev?.length === data.length &&
                prev?.every((item, index) =>
                    item.instance === data[index].instance &&
                    item.extras === data[index].extras
                );
            return isSame ? prev : data;
        });

        if (currentExtras) return;
        setCurrentExtras(data[0]);

    }, [supplementaryEventData]);

    const handleExtrasChange = (e) => {
        if (!e) return;
        const new_extras = extras.find((extra) => extra.instance.id == e.target.value);
        setCurrentExtras(new_extras);
    }

    if (!ordersDataAuth && !prevOrdersDataLength) return <LoadingIndicator type="page"/>

    // THIS IS SUPPLEMENTRY FILTERING

    const excludedEventNames = new Set([
    'ticketplan- festival theatre',
    'ticketplan- studio theatre',
    // add any other names to exclude here, normalized lowercase
    ]);

    const isExtraVisible = (e) => {
        // Normalize: exclude only if true or 'true'
        const excludeFlagRaw = e?.event?.attribute_ExcludeFromAccountUpsell;
        const isExcluded = excludeFlagRaw === true || excludeFlagRaw === 'true';

        const normalizedEventName = (e?.event?.name || '').trim().toLowerCase();

        return !isExcluded && !excludedEventNames.has(normalizedEventName);
    };

    // Only keep the ticket groups (one per upcoming booking) that have at
    // least one genuine, non-excluded extra attached - a booking with
    // nothing but TicketPlan insurance, or none at all, shouldn't be
    // selectable or counted towards showing this section.
    const visibleExtrasGroups = (extras || [])
        .map(group => ({
            ...group,
            extras: (group.extras || []).filter(isExtraVisible),
        }))
        .filter(group => group.extras.length > 0);

    const currentVisibleExtras =
        visibleExtrasGroups.find(
            group => group.instance?.id === currentExtras?.instance?.id,
        ) || visibleExtrasGroups[0];

    return (
        <>
            <AccountNavigation />

            <div className='booking-pathway__container booking-pathway__account booking-pathway__account--orders'>

                <div className='booking-pathway__main-content'>
                    <h1 className='booking-pathway__header'>Orders & tickets</h1>

                    <UpcomingTickets />

                    {extrasPending && visibleExtrasGroups.length > 0 && (
                        <section className='orders__extras'>
                            <header className='orders__extras--header'>
                                <h2 className='booking-pathway__header'>Enhance your experience</h2>
                            </header>

                            <LoadingIndicator />
                        </section>
                    )}

                    {(!extrasPending && visibleExtrasGroups.length > 0) ? (
                        <section className='orders__extras'>

                            <header className='orders__extras--header'>
                            <h2 className='booking-pathway__header'>Enhance your experience</h2>
                            {visibleExtrasGroups.length > 1 ? (
                                <select className='orders__extras--select' name="extras" id="extrasSelect" onChange={handleExtrasChange}>
                                    {visibleExtrasGroups.map((group) => {
                                        const formatted_date = UTILS.formatDateTime(group.instance?.instance.start);
                                        return (
                                            <option key={group.instance?.id} value={group.instance?.id}>
                                                {group.instance?.instance.event.name} {formatted_date}
                                            </option>
                                        );
                                    })}
                                </select>
                            ) : (
                                (() => {
                                    const formatted_date = UTILS.formatDateTime(currentVisibleExtras?.instance.instance.start);
                                    return <p className='orders__extras--date'>{currentVisibleExtras?.instance?.instance.event.name} {formatted_date}</p>;
                                })()
                            )}
                            </header>

                            <div className='extras-group orders__extras--group'>
                                {currentVisibleExtras?.extras?.map(instance => (
                                    <ExtrasInstance key={instance.id} instance={instance} isAccountPage={true} />
                                ))}
                            </div>
                        </section>
                    ) : null}

                    {((ordersDataAuth && ordersDataAuth.length > 0) || prevOrdersDataLength) && (
                        <>
                           <h2 className='booking-pathway__header past-orders-header'>Past orders</h2>
                            <p>Showing orders from {formatDate(dateRange.end, "dd MMM yyyy")} to {formatDate(dateRange.start, "dd MMM yyyy")}.</p>
                        </>
                    )}

                    {ordersDataAuth?.length ?
                        <section ref={accordionRef} className="past-orders accordion">
                            

                            {ordersDataAuth.map((order) => {
                                if (
                                    order.tickets?.length ||
                                    order.membershipSubscriptions?.length ||
                                    order.donations?.length ||
                                    order.giftVouchers?.length ||
                                    order.merchandiseItems?.length
                                ) {
                                    return <OrderContent key={order.id} order={order} />;
                                }
                                return null;
                            })}
                        </section>
                    :
                        <LoadingIndicator />
                    }
                    <div>
                        {/* only show load more if there are more orders to load but there will be one unecessary click as we can't know the length of the array */}
                        {ordersDataLength === prevOrdersDataLength ? null : (
                        <button onClick={loadMoreOrders} className='btn btn--primary'>
                            Load More
                            </button>
                        )}
                    </div>
                </div>

                <div className='booking-pathway__footer'>
                    <LogoutButton />

                    <UpsellBlock content={OPTIONS.account.orders_upsell} type="orders-upsell" />
                </div>

            </div>
        </>
    );
};

const OrderContent = ({order}) => {
    
    const orderDate = useMemo(() => {
        return format(parseISO(order.lastTransactionDate), "EEE d MMM h:mma")
    }, [order.lastTransactionDate]);

    // console.log("order pre-grouping",order);
    const groupedTickets = useMemo(() => {
        return UTILS.groupTicketsByInstance(order.tickets);
    }, [order.tickets]);

    
    const ticketsTotal = useMemo(() => {
        return UTILS.getGroupedTicketsTotal(groupedTickets);
    }, [groupedTickets]);

    
    const memershipsTotal = useMemo(() => {
        return UTILS.getMembershipsTotal(order.membershipSubscriptions);
    }, [order]);

    
    const donationsTotal = useMemo(() => {
        return UTILS.getDonationsTotal(order.donations);
    }, [order]);

    if (!order) return;

    return (
        <div key={order.id} className="accordion-item">

            <button className="accordion-item__header margin-trim"
                role="button"
                aria-expanded="false"
                aria-controls={order.id}>
                <h3 className="accordion-item__heading">
                    <span>{order.id}</span>
                    <span>{orderDate}</span>
                </h3>
                <span className="accordion-item__icon" aria-hidden="true">
                    <ArrowIcon />
                </span>
            </button>

            <div className="accordion-item__content-panel" id={order.id} aria-hidden="true">
                <div className="accordion-item__content-panel--inner">
                    <div className="accordion-item__content order-content">

                        {groupedTickets?.length ?
                            <section className='order-content__section order-content__section--tickets'>
                                <h4 className='order-content__header'>Tickets</h4>
                                <ul role="list" className='order-content__list'>
                                    {groupedTickets.map((ticket) => (
                                        <li key={ticket.instance_id}>
                                            <div className='order-content__list-item'>
                                                <header className='order-content__list-item--header'>
                                                    <p className='order-content__list-item--title'>{ticket.event_name}</p>
                                                    <p className='order-content__list-item--content'>{ticket.instance_date}</p>
                                                </header>
                                                <ul role="list" className='order-content__list-item--list'>
                                                    {ticket.tickets.map((instance) => (
                                                        <li key={instance.id} className='order-content__list-item--group'>
                                                            <p className='order-content__list-item--item'>
                                                                <span className='order-content__list-item--title'>Seat</span>
                                                                <span className='order-content__list-item--content'>{instance.seat?.name}</span>
                                                            </p>
                                                            <p className='order-content__list-item--item'>
                                                                <span className='order-content__list-item--title'>Type</span>
                                                                <span className='order-content__list-item--content'>{instance.ticketType?.name}</span>
                                                            </p>
                                                            <p className='order-content__list-item--item'>
                                                                <span className='order-content__list-item--title'>Total</span>
                                                                <span className='order-content__list-item--content'>£{instance.total?.toFixed(2)}</span>
                                                            </p>
                                                        </li>
                                                    ))}
                                                </ul>
                                                <p className='order-content__list-item--total'>
                                                    <span className='order-content__list-item--title'>Tickets Total</span>
                                                    <span className='order-content__list-item--content'>£{ticketsTotal?.toFixed(2)}</span>
                                                </p>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </section>
                            :null
                        }

                        {order.membershipSubscriptions?.length ?
                            <section className='order-content__section order-content__section--refunds'>
                                <h4 className='order-content__header'>Memberships</h4>
                                <ul role="list" className='order-content__list'>
                                    {order.membershipSubscriptions?.map((membership) => (
                                        <li key={membership.id} className='order-content__list-item--group'>
                                            <p className='order-content__list-item--item'>
                                                <span className='order-content__list-item--title'>Type</span>
                                                <span className='order-content__list-item--content'>{membership.membership?.name}</span>
                                            </p>
                                            <p className='order-content__list-item--item'>
                                                <span className='order-content__list-item--title'>Total</span>
                                                <span className='order-content__list-item--content'>£{membership.total?.toFixed(2)}</span>
                                            </p>
                                        </li>
                                    ))}
                                </ul>
                                <p className='order-content__list-item--total'>
                                    <span className='order-content__list-item--title'>Memberships Total</span>
                                    <span className='order-content__list-item--content'>£{memershipsTotal?.toFixed(2)}</span>
                                </p>
                            </section>
                            :null
                        }

                        {order.donations?.length ?
                            <section className='order-content__section order-content__section--refunds'>
                                <h4 className='order-content__header'>Donations</h4>
                                <ul role="list" className='order-content__list'>
                                    {order.donations?.map((donation) => (
                                        <li key={donation.id} className='order-content__list-item--group'>
                                            <p className='order-content__list-item--item'>
                                                <span className='order-content__list-item--title'>Type</span>
                                                <span className='order-content__list-item--content'>{donation.fund?.name}</span>
                                            </p>
                                            <p className='order-content__list-item--item'>
                                                <span className='order-content__list-item--title'>Total</span>
                                                <span className='order-content__list-item--content'>£{donation.amount?.toFixed(2)}</span>
                                            </p>
                                        </li>
                                    ))}
                                </ul>
                                <p className='order-content__list-item--total'>
                                    <span className='order-content__list-item--title'>Donation Total</span>
                                    <span className='order-content__list-item--content'>£{donationsTotal?.toFixed(2)}</span>
                                </p>
                            </section>
                            :null
                        }

                        {order.refunds?.length ?
                            <section className='order-content__section order-content__section--refunds'>
                                <h4 className='order-content__header'>Refunds</h4>
                                <ul role="list" className='order-content__list'>
                                    {order.refunds?.map((refund) => (
                                        <li key={refund.amount} className='order-content__list-item--group'>
                                            <p className='order-content__list-item--total'>
                                                <span className='order-content__list-item--title'>Refund Total</span>
                                                <span className='order-content__list-item--content'>£{refund.amount?.toFixed(2)}</span>
                                            </p>
                                        </li>
                                    ))}
                                </ul>
                            </section>
                            :null
                        }
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AccountOrders;
